/**
 * The arithmetic behind /word-count-goal-calculator.
 *
 * Zero-import leaf — this ships to the browser.
 *
 * Writing days are spread evenly rather than pinned to particular weekdays:
 * "four days a week" is a budget, not a schedule, and asking which four would
 * be a form worth nobody's time. Calendar days are therefore derived from the
 * ratio, which is symmetric in both directions.
 */

/** Whole days between two dates, ignoring time of day and DST. */
export function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / 86_400_000);
}

/** Parse a date input's `YYYY-MM-DD` as a local date, not UTC midnight. */
export function parseLocalDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export interface PaceInput {
  target: number;
  written: number;
  /** 1–7. */
  daysPerWeek: number;
}

export interface DeadlinePlan {
  remaining: number;
  calendarDays: number;
  writingDays: number;
  wordsPerDay: number;
  /** True when the deadline has passed or is today. */
  impossible: boolean;
  /** True when the target is already met. */
  done: boolean;
}

/** "I have a deadline" — how many words a day does it take? */
export function planForDeadline(
  { target, written, daysPerWeek }: PaceInput,
  start: Date,
  deadline: Date,
): DeadlinePlan {
  const remaining = Math.max(0, target - written);
  const calendarDays = daysBetween(start, deadline);
  const perWeek = Math.min(7, Math.max(1, daysPerWeek));
  // Round down: promising a writing day that the calendar does not contain is
  // how a plan quietly becomes unachievable.
  const writingDays = Math.max(0, Math.floor((calendarDays * perWeek) / 7));
  return {
    remaining,
    calendarDays,
    writingDays,
    wordsPerDay: writingDays > 0 ? Math.ceil(remaining / writingDays) : 0,
    impossible: remaining > 0 && writingDays <= 0,
    done: remaining === 0,
  };
}

export interface PacePlan {
  remaining: number;
  writingDays: number;
  calendarDays: number;
  finish: Date;
  done: boolean;
}

/** "I have a pace" — when does the draft finish? */
export function planForPace(
  { target, written, daysPerWeek }: PaceInput,
  start: Date,
  wordsPerDay: number,
): PacePlan | null {
  const remaining = Math.max(0, target - written);
  const perWeek = Math.min(7, Math.max(1, daysPerWeek));
  if (remaining === 0) {
    return { remaining: 0, writingDays: 0, calendarDays: 0, finish: start, done: true };
  }
  if (wordsPerDay <= 0) return null;
  const writingDays = Math.ceil(remaining / wordsPerDay);
  const calendarDays = Math.ceil((writingDays * 7) / perWeek);
  return {
    remaining,
    writingDays,
    calendarDays,
    finish: addDays(start, calendarDays),
    done: false,
  };
}

/** e.g. "about 3 months" — for the human-readable summary line. */
export function describeDuration(calendarDays: number): string {
  if (calendarDays <= 0) return "today";
  if (calendarDays === 1) return "1 day";
  if (calendarDays < 14) return `${calendarDays} days`;
  if (calendarDays < 60) {
    const weeks = Math.round(calendarDays / 7);
    return `about ${weeks} week${weeks === 1 ? "" : "s"}`;
  }
  if (calendarDays < 365) {
    const months = Math.round(calendarDays / 30.44);
    return `about ${months} month${months === 1 ? "" : "s"}`;
  }
  const years = Math.round((calendarDays / 365.25) * 10) / 10;
  return `about ${years} year${years === 1 ? "" : "s"}`;
}
